import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ItemMarketplaceDetailsMobile } from './ItemMarketplaceDetailsMobile';
import { ItemRecipientMobile } from './ItemRecipient';
import { EditType } from './EditType';
import { P2GBox, P2GButton, P2GDate, P2GLink } from '@parcel2go/core-ui';
import { ItemContentsMobile } from './ItemContents';
import { ServiceImage } from './ItemService';
import { P2GText } from '@parcel2go/core-ui';
import { P2GCheckBoxLegacy } from '@parcel2go/core-ui';
import { ItemActionType } from './SetMarketplaceAction';
import { GetExternalTrackingNumber } from '~/apps/smartsend/lib/helpers/SmartSendSummaryGenerator';
import { SettingsCache } from '~/lib/consts/settings-cache';
import LiveTrackingProgress from './LiveTrackingProgress';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import ECommerceLogo from '~/apps/account-linking/components/ECommerceLogo';
import { PrintInStoreExtra } from '~/lib/consts/extras';
import PrintInStoreQrCodesAutoLoad from '../edit/PrintInStoreQrCodesAutoLoad';
import { Suspense } from 'react';

interface props {
  item: SmartSendItemApiModel;
  toggleSelect: (val: boolean) => void;
  removeTag: (tag: string) => void;
  saveTags: (tags: string[]) => void;
  edit: (type: EditType) => void;
  action: (type: ItemActionType) => void;
  isReturn: boolean;
}

export default function SmartSendItemBookedMobile(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const edit = (type: EditType) => {
    props.edit?.(type == 'SMARTSEND-ORDER-TRACKING' ? 'SMARTSEND-ORDER-TRACKING' : 'SMARTSEND-ORDER-DETAILS');
  };

  const viewActions = () => {
    props.edit?.('SMARTSEND-ORDER-ACTIONS');
  };

  const labels = () => {
    props.edit?.('SMARTSEND-ORDER-LABELS');
  };

  const action = (type: ItemActionType) => {
    props.action && props.action(type);
  };

  const trackingNumber = GetExternalTrackingNumber(props.item);
  const sourceId = props.item.MarketplaceOrderLines?.[0]?.Source?.Id;
  const inTransit = !!(props.item.TrackingSummary?.Collected || props.item.TrackingSummary?.DroppedOff);
  const isPrintInStore = props.item.Extras.some((e) => e.Id === PrintInStoreExtra);

  return (
    <P2GBox p={1}>
      <P2GBox p={1} display="flex" gap={1} flexDirection="row" alignItems="center" justifyContent="space-between">
        {sourceId && <ECommerceLogo integration={sourceId} style={{ width: '80px' }} />}
        <P2GText variant="subtitle2" component="div" flexGrow={1}>
          {props.item.BookingDetails?.Reference ?? `ID:${props.item.Id}`}
          {trackingNumber && (
            <P2GText component="div" variant="caption">
              {trackingNumber}
            </P2GText>
          )}
        </P2GText>
        <P2GCheckBoxLegacy
          labelPlacement="start"
          id="Chk"
          onChange={props.toggleSelect}
          checked={props.item.State.selected}
          label={props.item.BookingDetails?.Service && <ServiceImage width={60} service={props.item.Service} />}
          variant="primary"
          error={false}
        />
      </P2GBox>
      {SettingsCache.settings.Features.includes('Tracking') && (
        <LiveTrackingProgress
          item={props.item}
          wrapper={(x) => (
            <P2GBox p={1}>
              {x}
              <P2GBox textAlign="center" sx={{ display: 'flex', mt: 1, justifyContent: 'space-between' }}>
                {props.item?.BookingDetails?.Created && (
                  <P2GText variant="body2">
                    <strong>Booked:</strong>{' '}
                    <P2GDate variant="body2" date={new Date(props.item.BookingDetails.Created)} />
                  </P2GText>
                )}
                <P2GLink onClick={() => props.edit('SMARTSEND-ORDER-TRACKING')} variant="body2">
                  {t('TRACKING_DETAILS_LINK', { defaultValue: 'Tracking Details' })}
                </P2GLink>
              </P2GBox>
            </P2GBox>
          )}
        />
      )}
      {!inTransit && isPrintInStore && (
        <Suspense>
          <PrintInStoreQrCodesAutoLoad item={props.item} wrapper={(x) => <P2GBox p={1}>{x}</P2GBox>} />
        </Suspense>
      )}
      <P2GBox p={1} backgroundColour="#f1f1f1">
        <ItemRecipientMobile
          variant="body2"
          edit={edit}
          customer={props.isReturn ? props.item.Sender : props.item.Recipient}
          revealText={t('VIEW_LINK', 'View')}
        />
      </P2GBox>
      <P2GBox p={1}>
        <ItemContentsMobile edit={edit} item={props.item} revealText={t('VIEW_LINK', 'View')} variant="body2" />
      </P2GBox>
      <ItemMarketplaceDetailsMobile
        action={action}
        item={props.item}
        removeTag={props.removeTag}
        saveTags={props.saveTags}
      />
      <P2GBox p={1} sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
        <P2GButton variant="tertiary" fullWidth onClick={viewActions}>
          {t('ACTIONS_LINK', { defaultValue: 'Actions' })}
        </P2GButton>
        {inTransit || isPrintInStore ? (
          <P2GButton variant="secondary" fullWidth onClick={() => edit('SMARTSEND-ORDER-DETAILS')}>
            {t('DETAILS_LINK', { defaultValue: 'Details' })}
          </P2GButton>
        ) : (
          <P2GButton variant="secondary" fullWidth onClick={labels}>
            {t('LABELS_LINK', { defaultValue: 'Labels' })}
          </P2GButton>
        )}
      </P2GBox>
    </P2GBox>
  );
}
